import { Link, useLocation, useNavigate } from "react-router-dom";
import { Instagram } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Logo } from "./Logo";

const CONTACT_EMAIL = "info@cabounstoppable.com";

const FooterLink = ({ to, children, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="text-sm text-white/60 hover:text-yellow-400 transition-colors"
  >
    {children}
  </Link>
);

const FooterSectionLink = ({ hash, children, testid }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const handleClick = (e) => {
    e.preventDefault();
    if (location.pathname === "/") {
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        window.history.replaceState(null, "", `/#${hash}`);
        return;
      }
    }
    navigate(`/#${hash}`);
  };
  return (
    <a
      href={`/#${hash}`}
      onClick={handleClick}
      data-testid={testid}
      className="text-sm text-white/60 hover:text-yellow-400 transition-colors"
    >
      {children}
    </a>
  );
};

export const Footer = () => {
  const { t } = useLanguage();

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-black text-white border-t border-white/10"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          {/* Brand */}
          <div className="md:col-span-5 flex flex-col gap-6">
            <Logo size="xl" className="-ml-1" />
            <p className="text-sm text-white/60 max-w-xs leading-relaxed">
              {t.footer.tagline}
            </p>
          </div>

          {/* Explore */}
          <div className="md:col-span-3">
            <h4 className="text-[10px] font-bold tracking-[0.3em] uppercase text-white/40 mb-6">
              {t.footer.explore}
            </h4>
            <ul className="space-y-3.5">
              <li>
                <FooterLink to="/" testid="footer-home">{t.nav.home}</FooterLink>
              </li>
              <li>
                <FooterLink to="/product/cabo-unstoppable-cap" testid="footer-original-cap">
                  {t.footer.originalCap}
                </FooterLink>
              </li>
              <li>
                <FooterSectionLink hash="cabo" testid="footer-cabo">
                  {t.nav.cabo}
                </FooterSectionLink>
              </li>
              <li>
                <FooterSectionLink hash="mindset" testid="footer-mindset">
                  {t.nav.mindset}
                </FooterSectionLink>
              </li>
              <li>
                <FooterLink to="/about" testid="footer-about">{t.nav.about}</FooterLink>
              </li>
              <li>
                <FooterLink to="/contact" testid="footer-contact">{t.nav.contact}</FooterLink>
              </li>
            </ul>
          </div>

          {/* Connect */}
          <div className="md:col-span-2">
            <h4 className="text-[10px] font-bold tracking-[0.3em] uppercase text-white/40 mb-6">
              {t.footer.connect}
            </h4>
            <ul className="space-y-3.5">
              <li>
                <a
                  href="https://www.instagram.com/cabounstoppable/"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-instagram"
                  className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-yellow-400 transition-colors"
                >
                  <Instagram className="w-4 h-4" strokeWidth={1.6} />
                  Instagram
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  data-testid="footer-email"
                  className="text-sm text-white/60 hover:text-yellow-400 transition-colors break-all"
                >
                  {CONTACT_EMAIL}
                </a>
              </li>
            </ul>
          </div>

          {/* Location */}
          <div className="md:col-span-2">
            <h4 className="text-[10px] font-bold tracking-[0.3em] uppercase text-white/40 mb-6">
              {t.footer.location}
            </h4>
            <p className="text-sm text-white/60 tracking-[0.15em] uppercase">
              Los Cabos · BCS · MX
            </p>
            <p className="mt-2 text-xs text-white/35 tracking-[0.2em]">
              22.89° N · 109.92° W
            </p>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-16 md:mt-20 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/40">
          <p data-testid="footer-copyright">
            © {new Date().getFullYear()} Cabo Unstoppable. {t.footer.rights}
          </p>
          <p className="uppercase tracking-[0.35em] text-white/30">Be Unstoppable</p>
        </div>
      </div>
    </footer>
  );
};
